import {
  listSaleEmails,
  useManualMatchSaleEmail,
  useUnlinkSaleEmail,
  useGetSaleEmail,
  getListSaleEmailsQueryKey,
  getGetSaleEmailQueryKey,
  type SaleEmailListItem,
  type InventoryListing,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Link2, ArrowLeftRight, Mail, X, AlertTriangle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useRef, useState, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// ─── Full-pagination fetcher ──────────────────────────────────────────────────
// The /emails endpoint defaults to limit=100. We fetch all pages in parallel
// after the first request tells us the total count.

const PAGE_SIZE = 500;

async function fetchAllSaleEmails(): Promise<SaleEmailListItem[]> {
  const first = await listSaleEmails({ limit: PAGE_SIZE, offset: 0 });
  const items: SaleEmailListItem[] = [...first.items];
  const total = first.total;
  if (total > PAGE_SIZE) {
    const extraPages = Math.ceil((total - PAGE_SIZE) / PAGE_SIZE);
    const rest = await Promise.all(
      Array.from({ length: extraPages }, (_, i) =>
        listSaleEmails({ limit: PAGE_SIZE, offset: (i + 1) * PAGE_SIZE }),
      ),
    );
    for (const page of rest) items.push(...page.items);
  }
  return items;
}

// ─── Date proximity helpers ───────────────────────────────────────────────────
// Compute the absolute day-distance between the email's event date and the
// inventory row's event date so we can add a tie-breaking proximity score.

function parseDateMs(s: string | null | undefined): number | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function dateProximityDays(
  email: SaleEmailListItem,
  row: InventoryListing,
): number | null {
  const emailMs = parseDateMs(email.eventDateText);
  const rowMs = parseDateMs(row.eventDate);
  if (emailMs == null || rowMs == null) return null;
  return Math.abs(emailMs - rowMs) / (1000 * 60 * 60 * 24);
}

// ─── Scoring ──────────────────────────────────────────────────────────────────
// Rank: account email match → event name → date proximity → section/row →
// seat overlap → quantity.

function scoreEmail(email: SaleEmailListItem, row: InventoryListing): number {
  let score = 0;

  // Account email — exact match (most discriminating signal)
  if (email.recipientEmail && row.vendorAccountEmail) {
    if (email.recipientEmail.toLowerCase() === row.vendorAccountEmail.toLowerCase())
      score += 40;
  }

  // Event name — exact or fuzzy substring
  if (email.eventName && row.eventName) {
    const a = email.eventName.toLowerCase();
    const b = row.eventName.toLowerCase();
    if (a === b) score += 30;
    else if (a.includes(b) || b.includes(a)) score += 15;
  }

  // Date proximity — tighter windows score higher
  const days = dateProximityDays(email, row);
  if (days !== null) {
    if (days === 0) score += 15;
    else if (days < 1) score += 12;
    else if (days <= 3) score += 8;
    else if (days <= 7) score += 4;
    // More than a week apart scores 0 for proximity
  }

  // Section and row — exact
  if (email.section && row.section && email.section.toLowerCase() === row.section.toLowerCase())
    score += 5;
  if (email.row && row.row && email.row.toLowerCase() === row.row.toLowerCase())
    score += 5;

  // Seat overlap
  if (email.seats && email.seats.length > 0) {
    const rowSeats = new Set(
      [row.seatFrom, row.seatTo].filter(Boolean).map((s) => s!.toLowerCase()),
    );
    if (rowSeats.size > 0) {
      const overlap = email.seats.filter((s) => rowSeats.has(s.toLowerCase()));
      if (overlap.length === email.seats.length) score += 10;
      else if (overlap.length > 0) score += 5;
    }
  }

  // Quantity — exact
  if (email.quantity != null && row.availableQuantity != null && email.quantity === row.availableQuantity)
    score += 5;

  return score;
}

// ─── Match quality indicators ─────────────────────────────────────────────────
// Per-cell colour: green = exact match, amber = close/partial, faded = mismatch.

type Quality = "match" | "close" | "mismatch";

function acctQuality(email: SaleEmailListItem, row: InventoryListing): Quality {
  if (!email.recipientEmail || !row.vendorAccountEmail) return "mismatch";
  return email.recipientEmail.toLowerCase() === row.vendorAccountEmail.toLowerCase()
    ? "match"
    : "mismatch";
}

function eventQuality(email: SaleEmailListItem, row: InventoryListing): Quality {
  if (!email.eventName || !row.eventName) return "mismatch";
  const a = email.eventName.toLowerCase();
  const b = row.eventName.toLowerCase();
  if (a === b) return "match";
  if (a.includes(b) || b.includes(a)) return "close";
  return "mismatch";
}

function sectionQuality(email: SaleEmailListItem, row: InventoryListing): Quality {
  const secMatch =
    email.section && row.section
      ? email.section.toLowerCase() === row.section.toLowerCase()
      : false;
  const rowMatch =
    email.row && row.row
      ? email.row.toLowerCase() === row.row.toLowerCase()
      : false;
  if (!email.section && !email.row) return "mismatch";
  if (secMatch && (rowMatch || !email.row || !row.row)) return "match";
  if (secMatch || rowMatch) return "close";
  return "mismatch";
}

function seatQuality(email: SaleEmailListItem, row: InventoryListing): Quality {
  if (!email.seats || email.seats.length === 0) return "mismatch";
  const rowSeats = [row.seatFrom, row.seatTo]
    .filter(Boolean)
    .map((s) => s!.toLowerCase());
  if (rowSeats.length === 0) return "mismatch";
  const overlap = email.seats.filter((s) => rowSeats.includes(s.toLowerCase()));
  if (overlap.length === email.seats.length && email.seats.length === rowSeats.length)
    return "match";
  if (overlap.length > 0) return "close";
  return "mismatch";
}

function qtyQuality(email: SaleEmailListItem, row: InventoryListing): Quality {
  if (email.quantity == null || row.availableQuantity == null) return "mismatch";
  return email.quantity === row.availableQuantity ? "match" : "mismatch";
}

const QUALITY_CELL: Record<Quality, string> = {
  match: "bg-emerald-500/15 text-emerald-300",
  close: "bg-amber-500/10 text-amber-300",
  mismatch: "text-muted-foreground/60",
};

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, string> = {
  pending: "bg-zinc-700/50 text-zinc-400 border-zinc-600/40",
  unmatched: "bg-zinc-700/50 text-zinc-400 border-zinc-600/40",
  no_match: "bg-zinc-700/50 text-zinc-400 border-zinc-600/40",
  matched: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  sold_out: "bg-purple-500/15 text-purple-300 border-purple-500/40",
  partial: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  ambiguous: "bg-orange-500/15 text-orange-300 border-orange-500/40",
  quantity_conflict: "bg-red-500/15 text-red-300 border-red-500/40",
  parse_error: "bg-red-500/15 text-red-300 border-red-500/40",
};

function statusStyle(s: string): string {
  return STATUS_STYLE[s] ?? "bg-muted text-muted-foreground border-border";
}

function statusLabel(s: string): string {
  switch (s) {
    case "pending":
    case "no_match":
      return "unmatched";
    case "sold_out":
      return "sold out";
    case "quantity_conflict":
      return "qty conflict";
    case "parse_error":
      return "parse error";
    default:
      return s;
  }
}

// ─── Search filtering ─────────────────────────────────────────────────────────

function emailSearchText(email: SaleEmailListItem): string {
  return [
    email.recipientEmail,
    email.eventName,
    email.eventDateText,
    email.section,
    email.row,
    ...(email.seats ?? []),
    email.listingNumber,
    email.emailSubject,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

// ─── Row height constant for virtualizer ──────────────────────────────────────

const ROW_HEIGHT = 56;

// ─── Email date formatter ─────────────────────────────────────────────────────

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── In-modal email slideout ──────────────────────────────────────────────────
// Renders the full email content (header meta + markdown body) as a side panel
// inside the picker dialog. Uses the same hook + ReactMarkdown approach as
// EmailViewerModal so rendering quality matches the standalone viewer.

function EmailSlideout({
  emailId,
  onClose,
}: {
  emailId: number;
  onClose: () => void;
}) {
  const { data: detail, isLoading, isError } = useGetSaleEmail(emailId, {
    query: {
      enabled: true,
      queryKey: getGetSaleEmailQueryKey(emailId),
    },
  });

  const email = detail?.email;

  const body = (() => {
    if (isLoading) {
      return (
        <div className="space-y-2.5 p-4">
          <Skeleton className="h-3.5 w-3/4" />
          <Skeleton className="h-3.5 w-full" />
          <Skeleton className="h-3.5 w-5/6" />
          <Skeleton className="h-3.5 w-2/3" />
          <Skeleton className="h-3.5 w-full" />
          <Skeleton className="h-3.5 w-1/2" />
        </div>
      );
    }
    if (isError) {
      return (
        <div className="m-4 flex flex-col items-center justify-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-5 text-center text-xs text-destructive">
          <AlertTriangle className="h-4 w-4" />
          <p>Couldn&apos;t load this email.</p>
        </div>
      );
    }
    if (!detail) {
      return (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        </div>
      );
    }
    if (!detail.rawBody) {
      return (
        <div className="m-4 rounded-md border border-border bg-card p-4 text-xs text-muted-foreground">
          No email body available.
        </div>
      );
    }
    return (
      <div className="p-4">
        <div className="rounded-md border border-border bg-card p-3">
          <div className="prose prose-sm prose-invert max-w-none leading-relaxed prose-headings:text-foreground prose-p:my-1.5 prose-a:text-primary prose-strong:text-foreground prose-li:my-0.5 prose-table:text-xs">
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer" />
                ),
              }}
            >
              {detail.rawBody}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    );
  })();

  return (
    <div className="flex w-80 shrink-0 flex-col overflow-hidden border-l border-border/60">
      {/* Slideout header */}
      <div className="flex items-center justify-between border-b border-border/50 bg-muted/20 px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[11px] font-semibold text-foreground">
            {email?.emailSubject ?? (isLoading ? "Loading…" : "Email")}
          </div>
          {email && (
            <div className="mt-0.5 grid grid-cols-[36px_1fr] gap-x-2 text-[10px] text-muted-foreground">
              <span>From</span>
              <span className="truncate">Ticketmaster</span>
              <span>To</span>
              <span className="truncate">{email.recipientEmail ?? "—"}</span>
              <span>Date</span>
              <span className="truncate">{fmtDate(email.receivedAt)}</span>
              <span>Listing</span>
              <span className="font-mono">#{email.listingNumber ?? "—"}</span>
            </div>
          )}
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="ml-2 h-6 w-6 shrink-0"
          onClick={onClose}
          aria-label="Close email preview"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      {/* Slideout body */}
      <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface SalePickerModalProps {
  row: InventoryListing;
  open: boolean;
  onClose: () => void;
  invalidate: () => void;
}

export function SalePickerModal({ row, open, onClose, invalidate }: SalePickerModalProps) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [slideoutEmailId, setSlideoutEmailId] = useState<number | null>(null);

  // Fetch ALL sale emails using a paginated all-pages fetcher so no candidates
  // are silently omitted. The query is disabled until the modal is opened.
  const { data: allEmails = [], isLoading } = useQuery({
    queryKey: [...getListSaleEmailsQueryKey(), "sale-picker-all"],
    queryFn: fetchAllSaleEmails,
    enabled: open,
    staleTime: 30_000,
  });

  const manualMatch = useManualMatchSaleEmail();
  const unlink = useUnlinkSaleEmail();

  const emails = useMemo(
    () =>
      allEmails.filter(
        (e) =>
          e.emailType === "sale_notification" || e.emailType === "sale_confirmation",
      ),
    [allEmails],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pool = emails.filter((email) => {
      if (email.match?.inventoryId === row.id) return false;
      const isLinkedElsewhere =
        email.match &&
        email.match.matchStatus !== "no_match" &&
        email.match.inventoryId != null;
      const isUnmatched =
        !email.match ||
        email.match.matchStatus === "no_match" ||
        email.derivedStatus === "pending";
      return isUnmatched || isLinkedElsewhere;
    });
    const searched = q ? pool.filter((e) => emailSearchText(e).includes(q)) : pool;
    return searched
      .map((e) => ({ email: e, score: scoreEmail(e, row) }))
      .sort((a, b) => b.score - a.score)
      .map(({ email }) => email);
  }, [emails, row, search]);

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  const handleClose = useCallback(() => {
    setSearch("");
    setBusyId(null);
    setSlideoutEmailId(null);
    onClose();
  }, [onClose]);

  const isSwap = (email: SaleEmailListItem): boolean => {
    if (!email.match) return false;
    if (email.match.matchStatus === "no_match" || email.derivedStatus === "pending")
      return false;
    if (email.match.inventoryId == null) return false;
    return true;
  };

  const handleLink = useCallback(
    async (email: SaleEmailListItem) => {
      setBusyId(email.id);
      try {
        if (isSwap(email)) {
          await unlink.mutateAsync({ id: email.id });
        }
        await manualMatch.mutateAsync({ id: email.id, data: { inventoryId: row.id } });
        qc.invalidateQueries({ queryKey: getListSaleEmailsQueryKey() });
        invalidate();
        toast.success("Sale email linked.");
        handleClose();
      } catch {
        toast.error("Failed to link email — please try again.");
      } finally {
        setBusyId(null);
      }
    },
    [row.id, manualMatch, unlink, qc, invalidate, handleClose],
  );

  const seatContext = [
    row.section,
    row.row,
    row.seatsDisplay || [row.seatFrom, row.seatTo].filter(Boolean).join("-"),
  ]
    .filter(Boolean)
    .join(" / ");

  const tableHeight = 400;
  const slideoutOpen = slideoutEmailId != null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className={cn(
          "w-full p-0 gap-0 overflow-hidden transition-[max-width] duration-200",
          slideoutOpen ? "max-w-5xl" : "max-w-3xl",
        )}
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border/50">
          <DialogTitle className="text-sm font-semibold leading-tight">
            Link Sale Email
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground mt-0.5 leading-snug">
            <span className="font-medium text-foreground/80">
              {row.eventName ?? "Unknown Event"}
            </span>
            {seatContext ? (
              <span className="ml-2 font-mono text-[11px] bg-muted/50 rounded px-1.5 py-0.5 border border-border/50">
                {seatContext}
              </span>
            ) : null}
            {row.vendorAccountEmail ? (
              <span className="ml-2 text-muted-foreground/70">
                {row.vendorAccountEmail}
              </span>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {/* Main flex area: table + optional slideout */}
        <div className="flex min-h-0 overflow-hidden">
          {/* Left: table column */}
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border/40">
              <Input
                placeholder="Filter by account, event, section, seats…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-7 text-xs"
              />
            </div>

            {/* Column headers */}
            <div className="grid grid-cols-[1fr_1fr_0.7fr_0.5fr_0.35fr_0.6fr_0.6fr] gap-x-2 px-4 py-1.5 border-b border-border/40 bg-muted/30 text-[10px] font-medium text-muted-foreground uppercase tracking-wide select-none">
              <div>Account</div>
              <div>Event / Date</div>
              <div>Section / Row</div>
              <div>Seats</div>
              <div>Qty</div>
              <div>Status</div>
              <div className="text-right">Action</div>
            </div>

            {/* Virtualized rows */}
            <div
              ref={parentRef}
              style={{ height: tableHeight, overflowY: "auto" }}
              className="relative"
            >
              {isLoading ? (
                <div className="flex items-center justify-center h-full text-muted-foreground text-xs gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading emails…
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex items-center justify-center h-full text-muted-foreground text-xs">
                  {search ? "No emails match your search." : "No eligible sale emails found."}
                </div>
              ) : (
                <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
                  {virtualizer.getVirtualItems().map((vitem) => {
                    const email = filtered[vitem.index];
                    const swap = isSwap(email);
                    const busy = busyId === email.id;
                    const acctQ = acctQuality(email, row);
                    const evtQ = eventQuality(email, row);
                    const secQ = sectionQuality(email, row);
                    const seatsQ = seatQuality(email, row);
                    const qtyQ = qtyQuality(email, row);
                    const status = email.derivedStatus;
                    const isSelected = slideoutEmailId === email.id;

                    return (
                      <div
                        key={email.id}
                        style={{
                          position: "absolute",
                          top: 0,
                          transform: `translateY(${vitem.start}px)`,
                          height: ROW_HEIGHT,
                          width: "100%",
                        }}
                        className={cn(
                          "grid grid-cols-[1fr_1fr_0.7fr_0.5fr_0.35fr_0.6fr_0.6fr] items-center gap-x-2 px-4 border-b border-border/30 text-[11px]",
                          busy && "opacity-60",
                          isSelected && "bg-muted/30",
                        )}
                      >
                        {/* Account */}
                        <div
                          className={cn("truncate font-mono text-[10px]", QUALITY_CELL[acctQ])}
                          title={email.recipientEmail ?? ""}
                        >
                          {email.recipientEmail ?? "—"}
                        </div>

                        {/* Event / Date */}
                        <div className={cn("min-w-0", QUALITY_CELL[evtQ])}>
                          <div className="truncate leading-tight">{email.eventName ?? "—"}</div>
                          {email.eventDateText && (
                            <div className="text-[10px] opacity-70 truncate">
                              {email.eventDateText}
                            </div>
                          )}
                        </div>

                        {/* Section / Row */}
                        <div
                          className={cn("truncate font-mono text-[10px]", QUALITY_CELL[secQ])}
                        >
                          {[email.section, email.row].filter(Boolean).join(" / ") || "—"}
                        </div>

                        {/* Seats */}
                        <div
                          className={cn("truncate font-mono text-[10px]", QUALITY_CELL[seatsQ])}
                          title={email.seats?.join(" | ") ?? ""}
                        >
                          {email.seats && email.seats.length > 0
                            ? email.seats.join(" | ")
                            : "—"}
                        </div>

                        {/* Qty */}
                        <div
                          className={cn(
                            "text-center font-mono text-[10px]",
                            QUALITY_CELL[qtyQ],
                          )}
                        >
                          {email.quantity ?? "—"}
                        </div>

                        {/* Status */}
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[9px] px-1.5 py-0 h-4 font-normal capitalize leading-none",
                              statusStyle(status),
                            )}
                          >
                            {statusLabel(status)}
                          </Badge>
                          {swap && email.match?.inventoryId != null && (
                            <span className="text-[9px] text-muted-foreground/60 truncate">
                              → listing #{email.match.inventoryId}
                            </span>
                          )}
                        </div>

                        {/* Action */}
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className={cn(
                              "h-6 w-6 text-muted-foreground/60 hover:text-foreground",
                              isSelected && "text-primary hover:text-primary",
                            )}
                            title="Preview email"
                            onClick={() =>
                              setSlideoutEmailId(isSelected ? null : email.id)
                            }
                          >
                            <Mail className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant={swap ? "outline" : "default"}
                            className={cn(
                              "h-6 text-[10px] px-2 gap-1",
                              !swap && "bg-emerald-600 hover:bg-emerald-700 text-white border-0",
                            )}
                            disabled={busy || busyId != null}
                            onClick={() => handleLink(email)}
                          >
                            {busy ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : swap ? (
                              <>
                                <ArrowLeftRight className="h-3 w-3" />
                                Swap
                              </>
                            ) : (
                              <>
                                <Link2 className="h-3 w-3" />
                                Link
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-4 py-2 border-t border-border/40 text-[10px] text-muted-foreground/60 flex justify-between">
              <span>
                {filtered.length} email{filtered.length !== 1 ? "s" : ""}
                {allEmails.length > 0 && ` of ${emails.length} total`}
              </span>
              <span>Sorted by best match · green = exact · amber = close</span>
            </div>
          </div>

          {/* Right: email slideout */}
          {slideoutOpen && (
            <EmailSlideout
              emailId={slideoutEmailId!}
              onClose={() => setSlideoutEmailId(null)}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
